import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Users, Search, Loader2, User } from "lucide-react";
import { PaginationParams, Patient } from "@/lib/types";
import { practitionerService } from "@/services/practitionerService";
import { format } from "date-fns";
import { formatDOB } from '@/lib/dateUtils';
import { Input } from "@/components/ui/input";
import SmartPagination from "../ui/smart-pagination";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getDefaultContactValue } from "@/lib/utils";

interface PractitionerPatientsProps {
  practitionerId: string;
  clinicId?: string;
}

const PractitionerPatients: React.FC<PractitionerPatientsProps> = ({
  practitionerId,
  clinicId,
}) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationParams>({
    search: "",
    page: 1,
    limit: 10,
    totalItems: 0,
  });

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await practitionerService.getPractitionerPatients(
        practitionerId,
        { ...pagination, clinic_id: clinicId }
      );
      setPatients(response.data?.patients);
      setPagination((prev) => ({ ...prev, ...response.data?.pagination }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, page: 1, search: search }));
    }, 800);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchPatients();
  }, [pagination.page, pagination.limit, pagination.search, clinicId]);

  const currentPage = pagination.page || 1;

  // Color rotation for user icons
  const colors = [
    { bg: "bg-blue-100", text: "text-blue-600" },
    { bg: "bg-purple-100", text: "text-purple-600" },
    { bg: "bg-pink-100", text: "text-pink-600" },
    { bg: "bg-orange-100", text: "text-orange-600" },
    { bg: "bg-amber-100", text: "text-amber-600" },
    { bg: "bg-cyan-100", text: "text-cyan-600" },
    { bg: "bg-indigo-100", text: "text-indigo-600" },
    { bg: "bg-rose-100", text: "text-rose-600" },
    { bg: "bg-violet-100", text: "text-violet-600" },
    { bg: "bg-sky-100", text: "text-sky-600" },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Patient List</h2>

        {/* Search */}
        <div className="relative w-full sm:max-w-md flex-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            type="text"
            className="pl-9 h-9 bg-white border-slate-200"
            placeholder="Search by name, email, phone, patient ID, or DOB"
            value={search}
            onChange={(e) => setSearch(e.target.value.trim())}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-sm">
            <thead style={{ backgroundColor: "rgb(245, 247, 247)" }}>
              <tr className="border-b border-slate-200">
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider w-12"
                  style={{ fontSize: "0.7rem" }}
                >
                  #
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: "0.7rem" }}
                >
                  Patient Name
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: "0.7rem" }}
                >
                  Date of Birth
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: "0.7rem" }}
                >
                  Email
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRowSkeleton key={index} />
                ))
              ) : patients.length === 0 ? (
                <tr>
                  <td colSpan={4} className="h-48 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <Users className="h-7 w-7 text-slate-400" />
                      </div>
                      <p className="font-medium text-slate-600">
                        {pagination.search
                          ? "No patients found"
                          : "No patients yet"}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        {pagination.search
                          ? "Try adjusting your search"
                          : "Patients assigned to this practitioner will appear here"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                patients.map((patient, index) => {
                  const colorIndex = index % colors.length;
                  const iconColor = colors[colorIndex];
                  const email = getDefaultContactValue(patient.contacts, 'email');

                  return (
                    <tr
                      key={patient.id}
                      className="hover:bg-slate-50/80 transition-colors border-b border-slate-100"
                    >
                      <td
                        className="px-4 py-3 text-slate-500"
                        style={{ fontSize: "0.8rem" }}
                      >
                        #
                        {String(
                          (currentPage - 1) * (pagination.limit || 10) +
                            index +
                            1
                        ).padStart(3, "0")}
                      </td>
                      <td className="px-4 py-3" style={{ fontSize: "0.8rem" }}>
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 ${iconColor.bg} rounded-lg flex items-center justify-center flex-shrink-0`}
                          >
                            <User className={`h-4 w-4 ${iconColor.text}`} />
                          </div>
                          <TooltipProvider delayDuration={300}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-medium text-slate-900 truncate block max-w-[180px] cursor-default capitalize">
                                  {patient.full_name}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-[300px] text-xs bg-black text-white border-black [&>svg]:fill-black"
                              >
                                {patient.full_name}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </td>
                      <td
                        className="px-4 py-3 text-slate-900"
                        style={{ fontSize: "0.8rem" }}
                      >
                        {formatDOB(patient.dob)}
                      </td>
                      <td
                        className="px-4 py-3 text-slate-900 max-w-[200px]"
                        style={{ fontSize: "0.8rem" }}
                      >
                        <span className="block truncate" title={email || "--"}>
                          {email || "--"}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with pagination */}
        {!loading && patients.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
            <p className="text-xs text-slate-500 whitespace-nowrap">
              Showing{" "}
              {pagination.page! * pagination.limit! - pagination.limit! + 1} -{" "}
              {pagination.page! * pagination.limit! > pagination.totalItems!
                ? pagination.totalItems
                : pagination.page! * pagination.limit!}{" "}
              of {pagination.totalItems} patients
            </p>
            {pagination.totalPages && pagination.totalPages > 1 && (
              <SmartPagination
                currentPage={currentPage}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, page: page }))
                }
                totalPages={pagination.totalPages}
                maxVisiblePages={3}
                className="mx-0 justify-center sm:justify-end"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TableRowSkeleton = () => {
  return (
    <tr className="border-b border-slate-100">
      <td className="px-4 py-3">
        <Skeleton className="w-8 h-4" />
      </td>
      <td className="px-4 py-3">
        <Skeleton className="w-20 h-4" />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Skeleton className="w-8 h-8 rounded-lg" />
          <Skeleton className="w-32 h-4" />
        </div>
      </td>
      <td className="px-4 py-3">
        <Skeleton className="w-24 h-4" />
      </td>
      <td className="px-4 py-3">
        <Skeleton className="w-40 h-4" />
      </td>
    </tr>
  );
};

export default PractitionerPatients;
